import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import "./Result.css";

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;
  
  try {
    if (cardCode.length < 3) {
      return null;
    }
    
    // Use cards folder with full card code (e.g., "10HH.png", "ASS.png")
    // Format: rank + suit (DD, SS, CC, HH)
    return `/assets/img/cards/${cardCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to parse card string and split into Dragon and Tiger cards
const parseCards = (cardString) => {
  if (!cardString) return { dragon: null, tiger: null };
  
  const cards = cardString.split(',');
  const dragonCard = cards[0] ? getCardImage(cards[0].trim()) : null;
  const tigerCard = cards[1] ? getCardImage(cards[1].trim()) : null;
  
  return { dragon: dragonCard, tiger: tigerCard };
};

// Helper function to parse rdesc for war: "Dragon#No#D : Even  |  T : Odd#D : Red  |  T : Black#D : Heart  |  T : Spade"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  
  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  const pair = parts[1] || "No";
  
  // Part 2: Odd/Even - "D : Even  |  T : Odd"
  const oddEvenPart = parts[2] || "";
  const oddEvenMatch = oddEvenPart.match(/D\s*:\s*(\w+)\s*\|\s*T\s*:\s*(\w+)/);
  const dragonOddEven = oddEvenMatch ? oddEvenMatch[1].trim() : "";
  const tigerOddEven = oddEvenMatch ? oddEvenMatch[2].trim() : "";
  
  // Part 3: Color - "D : Red  |  T : Black"
  const colorPart = parts[3] || "";
  const colorMatch = colorPart.match(/D\s*:\s*(\w+)\s*\|\s*T\s*:\s*(\w+)/);
  const dragonColor = colorMatch ? colorMatch[1].trim() : "";
  const tigerColor = colorMatch ? colorMatch[2].trim() : "";
  
  // Part 4: Suit - "D : Heart  |  T : Spade"
  const suitPart = parts[4] || "";
  const suitMatch = suitPart.match(/D\s*:\s*(\w+)\s*\|\s*T\s*:\s*(\w+)/);
  const dragonSuit = suitMatch ? suitMatch[1].trim() : "";
  const tigerSuit = suitMatch ? suitMatch[2].trim() : "";
  
  return {
    winner,
    pair,
    dragonOddEven,
    tigerOddEven,
    dragonColor,
    tigerColor,
    dragonSuit,
    tigerSuit,
  };
};

const ResultWar = ({ 
  data = [], 
  gameData = {},
  gameType = 'war',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(gameType);
        if (response?.success && response?.data?.data?.res) {
          // Map win values: "1" = "D" (Dragon), "2" = "T" (Tiger)
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "D" : item.win === "2" ? "T" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [gameType]);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);
    
    const mid = resultItem?.mid;
    
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults(gameType, mid.toString());
        if (response?.success && response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        } else if (response?.success && response?.data?.t1) {
          setDetailResult(response.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  }, [gameType, detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal || !detailResult) return null;

    const cards = parseCards(detailResult.card);
    const rdescData = parseRdesc(detailResult.rdesc);
    const winner = detailResult.winnat || rdescData.winner || "";
    const winValue = detailResult.win;
    
    // Determine which side is the winner
    const isDragonWinner = winValue === "1" || winner.toLowerCase() === "dragon";
    const isTigerWinner = winValue === "2" || winner.toLowerCase() === "tiger";

    return {
      roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      dragon: {
        card: cards.dragon,
        isWinner: isDragonWinner,
      },
      tiger: {
        card: cards.tiger,
        isWinner: isTigerWinner,
      },
      resultDetails: {
        winner: winner,
        pair: rdescData.pair || "No",
        oddEven: `D : ${rdescData.dragonOddEven}  |  T : ${rdescData.tigerOddEven}`,
        color: `D : ${rdescData.dragonColor}  |  T : ${rdescData.tigerColor}`,
        suit: `D : ${rdescData.dragonSuit}  |  T : ${rdescData.tigerSuit}`,
      },
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0 
    ? lastResults
    : data.map((res, idx) => ({ win: res === "D" ? "D" : res === "T" ? "T" : res, mid: null }));

  return (
    <>
      <div className="result-row dragon-tiger casino-last-results">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const isDragon = win === "D" || win === "1";
          return (
            <div
              key={index}
              className={`result-circle ${isDragon ? "result-dragon" : "result-tiger"}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {isDragon ? "D" : "T"}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="War"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="row">
            <div className="col-12">
              <h6 className="text-right">
                <b>Round Id :</b> {modalData.roundId}
              </h6>
              <div className="text-center luckyseven-img">
                {modalData.dragon?.card && (
                  <img src={modalData.dragon.card} alt="Dragon Card" />
                )}
                {modalData.tiger?.card && (
                  <img src={modalData.tiger.card} alt="Tiger Card" />
                )}
              </div>
              <div className="board-result-inner m-t-5">
                {modalData.resultDetails.winner && (
                  <p className="m-b-0">
                    <b className="greenbx">Result:</b> {modalData.resultDetails.winner}
                  </p>
                )}
                <p className="m-b-0">
                  <b className="redbx">Pair:</b> {modalData.resultDetails.pair || 'No'}
                </p>
                {modalData.resultDetails.oddEven && (
                  <p className="m-b-0">
                    <b className="yellowbx">Odd/Even:</b> {modalData.resultDetails.oddEven}
                  </p>
                )}
                {modalData.resultDetails.color && (
                  <p className="m-b-0">
                    <b className="yellowbx">Color:</b> {modalData.resultDetails.color}
                  </p>
                )}
                {modalData.resultDetails.suit && (
                  <p className="m-b-0">
                    <b className="yellowbx">Suit:</b> {modalData.resultDetails.suit}
                  </p>
                )}
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultWar;

